import DashboardLayout from "@/components/DashboardLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { trpc } from "@/lib/trpc";
import { AlertTriangle, ClipboardList, Download, Plus, Search } from "lucide-react";
import { WorkInProgressPanel } from "@/components/roWorkTimer/WorkInProgressPanel";
import { formatDate } from "@/lib/dateUtils";
import { useState } from "react";
import { useLocation, useSearch } from "wouter";
import { usePathname, navigate } from "wouter/use-browser-location";
import * as XLSX from "xlsx";

const STATUS_FILTERS = [
  { label: "All", value: "" },
  { label: "Received", value: "received" },
  { label: "Diagnosis", value: "diagnosis" },
  { label: "Awaiting Confirmation", value: "awaiting_customer_confirmation" },
  { label: "In Progress", value: "in_progress" },
  { label: "Pending Parts", value: "pending_parts" },
  { label: "Repair Complete", value: "repair_complete" },
  { label: "Awaiting Payment", value: "awaiting_payment" },
  { label: "Awaiting Collection", value: "awaiting_collection" },
  { label: "Closed", value: "closed" },
];

export default function Jobs() {
  const [, setLocation] = useLocation();
  const pathname = usePathname();
  const search = useSearch();
  const params = new URLSearchParams(search);
  const defaultStatus = params.get("status") || "";

  const [statusFilter, setStatusFilter] = useState(defaultStatus);
  const [searchQuery, setSearchQuery] = useState("");

  const {
    data: jobs,
    isLoading: isLoadingJobs,
    error: jobsError,
  } = trpc.jobs.list.useQuery({
    status: statusFilter || undefined,
    limit: 200,
  });

  const {
    data: searchResults,
    isLoading: isLoadingSearch,
    error: searchError,
  } = trpc.jobs.search.useQuery(
    { query: searchQuery },
    { enabled: searchQuery.length > 1 }
  );

  const utils = trpc.useUtils();

  const usingSearch = searchQuery.length > 1;
  const displayJobs = usingSearch ? searchResults : jobs;
  const activeError = usingSearch ? searchError : jobsError;
  const isLoading = usingSearch ? isLoadingSearch : isLoadingJobs;

  const handleExport = async () => {
    const data = await utils.jobs.exportData.fetch({ status: statusFilter || undefined });
    if (!data || data.length === 0) return;
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Repair Orders");
    XLSX.writeFile(wb, `Clarity-Service-Jobs-${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Repair Orders</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {displayJobs?.length || 0} job{displayJobs?.length !== 1 ? "s" : ""}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Export</span>
            </Button>
            <Button size="sm" onClick={() => setLocation("/repair-orders/new")}>
              <Plus className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">New Job</span>
            </Button>
          </div>
        </div>

        <WorkInProgressPanel />

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search job number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex gap-1 flex-wrap">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setStatusFilter(f.value)}
                className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                  statusFilter === f.value
                    ? "bg-black text-white border-black"
                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Job List */}
        {activeError ? (
          <div className="text-center py-16 text-muted-foreground">
            <AlertTriangle className="h-10 w-10 mx-auto mb-3 text-red-500" />
            <p className="font-medium text-red-700">Failed to load repair orders</p>
            <p className="text-sm mt-1">{activeError.message || "Please retry."}</p>
            <Button
              size="sm"
              variant="outline"
              className="mt-4"
              onClick={() => {
                if (usingSearch) {
                  utils.jobs.search.invalidate({ query: searchQuery });
                } else {
                  utils.jobs.list.invalidate({ status: statusFilter || undefined, limit: 200 });
                }
              }}
            >
              Retry
            </Button>
          </div>
        ) : isLoading ? (
          <div className="space-y-3">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        ) : !displayJobs || displayJobs.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <ClipboardList className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No jobs found</p>
            <p className="text-sm mt-1">
              {statusFilter ? "Try a different status filter" : "Create the first repair order"}
            </p>
            {!statusFilter && (
              <Button size="sm" className="mt-4" onClick={() => setLocation("/repair-orders/new")}>
                New Job
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop table — hidden on mobile */}
            <Card className="hidden md:block">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide">Job #</th>
                        <th className="text-left px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide">Status</th>
                        <th className="text-left px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide">Customer</th>
                        <th className="text-left px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide">Reported By</th>
                        <th className="text-left px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide">Fault Reported</th>
                        <th className="text-left px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide">Collection</th>
                        <th className="text-left px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wide">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayJobs.map((job) => {
                        const daysSinceUpdate = Math.floor(
                          (Date.now() - new Date(job.updatedAt).getTime()) / (1000 * 60 * 60 * 24)
                        );
                        const isAging = daysSinceUpdate >= 7 && job.status !== "closed";
                        const closedStatuses = ["repair_complete", "awaiting_payment", "awaiting_collection", "closed"];
                        const slaDeadline = (job as any).slaExtendedUntil || (job as any).slaDeadline;
                        const now = Date.now();
                        const slaMs = slaDeadline ? new Date(slaDeadline).getTime() - now : null;
                        const slaDaysLeft = slaMs !== null ? Math.ceil(slaMs / (1000 * 60 * 60 * 24)) : null;
                        const showSla = !closedStatuses.includes(job.status) && slaDaysLeft !== null;
                        const slaColor = slaDaysLeft !== null && slaDaysLeft < 0 ? "text-red-600 bg-red-50" : slaDaysLeft !== null && slaDaysLeft <= 1 ? "text-amber-600 bg-amber-50" : "text-green-700 bg-green-50";
                        const slaLabel = slaDaysLeft !== null && slaDaysLeft < 0 ? `SLA Overdue ${Math.abs(slaDaysLeft)}d` : slaDaysLeft !== null ? `SLA ${slaDaysLeft}d left` : "";
                        return (
                          <tr
                            key={job.id}
                            className={`border-b last:border-0 hover:bg-gray-50 cursor-pointer ${isAging ? "bg-amber-50/50" : ""}`}
                            onClick={() => navigate(`/repair-orders/id/${job.id}`, { state: { from: pathname } })}
                          >
                            <td className="px-4 py-3">
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                  {isAging && <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />}
                                  <span className="font-mono font-medium text-xs">{job.jobNumber}</span>
                                </div>
                                {showSla && (
                                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded w-fit ${slaColor}`}>{slaLabel}</span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3"><StatusBadge status={job.status} /></td>
                            <td className="px-4 py-3 font-medium">{(job as any).customerName ?? "—"}</td>
                            <td className="px-4 py-3 text-muted-foreground">{job.reportedBy}</td>
                            <td className="px-4 py-3 max-w-xs">
                              <span className="truncate block text-muted-foreground">{job.faultReported}</span>
                            </td>
                            <td className="px-4 py-3 text-muted-foreground capitalize">
                              {job.collectionPreference === "delivery" ? "Delivery" : "Self Pick-Up"}
                            </td>
                            <td className="px-4 py-3 text-muted-foreground text-xs">{formatDate(job.createdAt)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Mobile card list — shown only on mobile */}
            <div className="md:hidden space-y-2">
              {displayJobs.map((job) => {
                const daysSinceUpdate = Math.floor(
                  (Date.now() - new Date(job.updatedAt).getTime()) / (1000 * 60 * 60 * 24)
                );
                const isAging = daysSinceUpdate >= 7 && job.status !== "closed";
                const closedStatuses = ["repair_complete", "awaiting_payment", "awaiting_collection", "closed"];
                const slaDeadline = (job as any).slaExtendedUntil || (job as any).slaDeadline;
                const now = Date.now();
                const slaMs = slaDeadline ? new Date(slaDeadline).getTime() - now : null;
                const slaDaysLeft = slaMs !== null ? Math.ceil(slaMs / (1000 * 60 * 60 * 24)) : null;
                const showSla = !closedStatuses.includes(job.status) && slaDaysLeft !== null;
                const slaColor = slaDaysLeft !== null && slaDaysLeft < 0 ? "text-red-600 bg-red-50" : slaDaysLeft !== null && slaDaysLeft <= 1 ? "text-amber-600 bg-amber-50" : "text-green-700 bg-green-50";
                const slaLabel = slaDaysLeft !== null && slaDaysLeft < 0 ? `SLA Overdue ${Math.abs(slaDaysLeft)}d` : slaDaysLeft !== null ? `SLA ${slaDaysLeft}d left` : "";
                return (
                  <Card
                    key={job.id}
                    className={`cursor-pointer hover:shadow-sm transition-shadow ${isAging ? "border-amber-200 bg-amber-50/30" : ""}`}
                    onClick={() => navigate(`/repair-orders/id/${job.id}`, { state: { from: pathname } })}
                  >
                    <CardContent className="p-3">
                      {/* Row 1: job number + status */}
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-1.5">
                          {isAging && <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />}
                          <span className="font-mono font-semibold text-sm">{job.jobNumber}</span>
                        </div>
                        <StatusBadge status={job.status} />
                      </div>
                      {/* Row 2: customer name */}
                      <p className="font-medium text-sm mt-1.5">{(job as any).customerName ?? "—"}</p>
                      {/* Row 3: fault reported */}
                      {job.faultReported && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{job.faultReported}</p>
                      )}
                      {/* Row 4: meta info */}
                      <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                        {job.reportedBy && (
                          <span className="text-xs text-muted-foreground">{job.reportedBy}</span>
                        )}
                        <span className="text-xs text-muted-foreground capitalize">
                          {job.collectionPreference === "delivery" ? "Delivery" : "Pick-Up"}
                        </span>
                        <span className="text-xs text-muted-foreground">{formatDate(job.createdAt)}</span>
                        {showSla && (
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${slaColor}`}>{slaLabel}</span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
